import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useProperty } from '../../context/PropertyContext';
import toast from 'react-hot-toast';

const PropertyForm = ({ property, onSuccess, onCancel }) => {
  const { addProperty, updateProperty } = useProperty();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUrls, setImageUrls] = useState(['']);
  const [features, setFeatures] = useState(['']);

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    if (property) {
      // Populate form with existing property data
      Object.keys(property).forEach(key => {
        if (key === 'images') {
          setImageUrls(property.images || ['']);
        } else if (key === 'features') {
          setFeatures(property.features || ['']);
        } else if (key !== 'id' && key !== 'agent') {
          setValue(key, property[key]);
        }
      });
      
      // Set agent data if exists
      if (property.agent) {
        setValue('agentName', property.agent.name);
        setValue('agentTitle', property.agent.title);
        setValue('agentImage', property.agent.image);
      }
    }
  }, [property, setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Process form data
      const propertyData = {
        ...data,
        price: parseInt(data.price),
        bedrooms: parseInt(data.bedrooms),
        bathrooms: parseInt(data.bathrooms),
        area: parseInt(data.area),
        garage: data.garage ? parseInt(data.garage) : 0,
        yearBuilt: data.yearBuilt ? parseInt(data.yearBuilt) : null,
        lotSize: data.lotSize ? parseFloat(data.lotSize) : null,
        images: imageUrls.filter(url => url.trim() !== ''),
        features: features.filter(feature => feature.trim() !== ''),
        badges: data.badges ? data.badges.split(',').map(b => b.trim()).filter(b => b) : [],
        agent: {
          name: data.agentName || 'TrueHome Agent',
          title: data.agentTitle || 'Real Estate Specialist',
          image: data.agentImage || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face'
        }
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (property) {
        updateProperty(property.id, propertyData);
        toast.success('Property updated successfully!');
      } else {
        addProperty(propertyData);
        toast.success('Property added successfully!');
      }

      reset();
      setImageUrls(['']);
      setFeatures(['']);
      onSuccess();
    } catch (error) {
      toast.error('Failed to save property. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addImageUrl = () => {
    setImageUrls([...imageUrls, '']);
  };

  const removeImageUrl = (index) => {
    setImageUrls(imageUrls.filter((_, i) => i !== index));
  };

  const updateImageUrl = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  const addFeature = () => {
    setFeatures([...features, '']);
  };

  const removeFeature = (index) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  return (
    <motion.div
      className="property-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-sections">
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Property Title *</label>
                <input
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-message">{errors.title.message}</span>}
              </div>

              <div className="form-group">
                <label>Property Type *</label>
                <select
                  {...register('type', { required: 'Type is required' })}
                  className={errors.type ? 'error' : ''}
                >
                  <option value="">Select Type</option>
                  <option value="BDA">BDA</option>
                  <option value="BRMDA">BRMDA</option>
                  <option value="DC Conversion">DC Conversion</option>
                  <option value="Revenue Sites">Revenue Sites</option>
                  <option value="Houses">Houses</option>
                  <option value="Land">Land</option>
                  <option value="Shed">Shed</option>
                  <option value="Agriculture Land">Agriculture Land</option>
                </select>
                {errors.type && <span className="error-message">{errors.type.message}</span>}
              </div>

              <div className="form-group full-width">
                <label>Location *</label>
                <input
                  type="text"
                  {...register('location', { required: 'Location is required' })}
                  className={errors.location ? 'error' : ''}
                />
                {errors.location && <span className="error-message">{errors.location.message}</span>}
              </div>

              <div className="form-group full-width">
                <label>Description *</label>
                <textarea
                  rows="4"
                  {...register('description', { required: 'Description is required' })}
                  className={errors.description ? 'error' : ''}
                ></textarea>
                {errors.description && <span className="error-message">{errors.description.message}</span>}
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="form-section">
            <h3>Property Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Price (₹) *</label>
                <input
                  type="number"
                  {...register('price', { required: 'Price is required', min: 1 })}
                  className={errors.price ? 'error' : ''}
                />
                {errors.price && <span className="error-message">{errors.price.message}</span>}
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  {...register('status', { required: 'Status is required' })}
                  className={errors.status ? 'error' : ''}
                >
                  <option value="">Select Status</option>
                  <option value="For Sale">For Sale</option>
                  <option value="Sold">Sold</option>
                  <option value="For Rent">For Rent</option>
                  <option value="Rented">Rented</option>
                </select>
                {errors.status && <span className="error-message">{errors.status.message}</span>}
              </div>

              <div className="form-group">
                <label>Bedrooms *</label>
                <input
                  type="number"
                  {...register('bedrooms', { required: 'Bedrooms is required', min: 1 })}
                  className={errors.bedrooms ? 'error' : ''}
                />
                {errors.bedrooms && <span className="error-message">{errors.bedrooms.message}</span>}
              </div>

              <div className="form-group">
                <label>Bathrooms *</label>
                <input
                  type="number"
                  {...register('bathrooms', { required: 'Bathrooms is required', min: 1 })}
                  className={errors.bathrooms ? 'error' : ''}
                />
                {errors.bathrooms && <span className="error-message">{errors.bathrooms.message}</span>}
              </div>

              <div className="form-group">
                <label>Area (sq ft) *</label>
                <input
                  type="number"
                  {...register('area', { required: 'Area is required', min: 1 })}
                  className={errors.area ? 'error' : ''}
                />
                {errors.area && <span className="error-message">{errors.area.message}</span>}
              </div>

              <div className="form-group">
                <label>Garage Spaces</label>
                <input
                  type="number"
                  {...register('garage')}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Year Built</label>
                <input
                  type="number"
                  {...register('yearBuilt')}
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>

              <div className="form-group">
                <label>Lot Size (acres)</label>
                <input
                  type="number"
                  step="0.1"
                  {...register('lotSize')}
                  min="0"
                />
              </div>

              <div className="form-group full-width">
                <label>Badges (comma-separated)</label>
                <input
                  type="text"
                  {...register('badges')}
                  placeholder="Featured, New, Exclusive"
                />
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3>Property Images</h3>
            <div className="dynamic-fields">
              {imageUrls.map((url, index) => (
                <div key={index} className="dynamic-field">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => updateImageUrl(index, e.target.value)}
                    placeholder="Enter image URL"
                  />
                  {imageUrls.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeImageUrl(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-btn"
                onClick={addImageUrl}
              >
                <i className="fas fa-plus"></i>
                Add Image URL
              </button>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h3>Features & Amenities</h3>
            <div className="dynamic-fields">
              {features.map((feature, index) => (
                <div key={index} className="dynamic-field">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    placeholder="Enter feature"
                  />
                  {features.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeFeature(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-btn"
                onClick={addFeature}
              >
                <i className="fas fa-plus"></i>
                Add Feature
              </button>
            </div>
          </div>

          {/* Agent Information */}
          <div className="form-section">
            <h3>Agent Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Agent Name</label>
                <input
                  type="text"
                  {...register('agentName')}
                />
              </div>

              <div className="form-group">
                <label>Agent Title</label>
                <input
                  type="text"
                  {...register('agentTitle')}
                />
              </div>

              <div className="form-group full-width">
                <label>Agent Image URL</label>
                <input
                  type="url"
                  {...register('agentImage')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="loading"></div>
                <span>{property ? 'Updating...' : 'Adding...'}</span>
              </>
            ) : (
              <>
                <span>{property ? 'Update Property' : 'Add Property'}</span>
                <i className="fas fa-save"></i>
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default PropertyForm;